import React, { useState } from 'react'
import Modal from '../../shared/modal'
import Icon from '../../shared/icon'
import BowlStandForm, { parseBowlAmount } from '../BowlStandForm'
import { ReplenishmentOrderAction, HUMAN_SLOTS } from '../types'
import { t } from 'src/i18n'

const scope = 'replenishment.bowls'

const ReplenishmentCompletedModalContent = ({ closeModal, amount, slotNumber, toggleSidebar }) => {

  const openBasket = () => {
    toggleSidebar(true)
    closeModal()
  }

  return (
    <div className='bowl-replenishment-complete'>
      <div className='title-icon'>
        <Icon name='replace'/>
      </div>
      <div className='heavy-text'>{t('bowls_header', { scope, slotNumber: HUMAN_SLOTS[slotNumber - 1] })}</div>
      <div className='plates-icon-container'>
        <Icon name='plate' color='white'/>
      </div>
      <p>
      {t('bowls_message', { scope, amount: amount })}
      </p>
      <div className='buttons'>
        <button className='btn btn-secondary' onClick={openBasket}>{t('replenishment.replenishment_modal.go_to_basket')}</button>
        <button className='btn btn-primary' onClick={closeModal}>{t('replenishment.replenishment_modal.continue')}</button>
      </div>
    </div>
  )
}

export const BowlReplenishmentModal = ({ containerSlot, container, closeModal, draftReplenishmentOrder, updateCart, toggleSidebar }) => {
  const maxAmount = container?.max_amount || 0
  const amountLeft = container?.remaining_amount ?? 0
  const freeCapacity = container?.replenishment_free_capacity ?? 0
  // Why the cap can sit well below the space visibly left on the stand.
  const alreadyOnOrder = container?.replenishment_on_order ?? 0

  const cartLine = draftReplenishmentOrder.plateReplenishments
    .find(replenishment => replenishment.slotNumber == containerSlot)

  const [completed, setCompleted] = useState(false)
  // Seeding free capacity over an existing line turns a glance-and-save into
  // a silent rewrite.
  const [replenishmentAmount, setReplenishmentAmount] = useState(cartLine?.amount ?? freeCapacity)
  const [saveError, setSaveError] = useState<string | null>(null)

  const changeReplenishmentAmount = (passedValue: string) => {
    const value = parseBowlAmount(passedValue)
    if (value === null) return

    setReplenishmentAmount(value)
    setSaveError(null)
  }

  const validationError = () => replenishmentAmount > freeCapacity
    ? t('exceeds_capacity', { scope: 'stock_control.plate_container', max: maxAmount, free: freeCapacity })
    : null

  // Another order may have claimed the stand's space since this screen
  // loaded, so the confirmation waits for the server.
  const saveReplenishment = () => {
    setSaveError(null)

    updatePlateReplenishments({ slotNumber: containerSlot, amount: replenishmentAmount })
      .then((error: string | null) => error ? setSaveError(error) : setCompleted(true))
  }

  const updatePlateReplenishments = (newPlateReplenishment) => {
    if(cartLine) {

      const updatedPlateReplenishments = draftReplenishmentOrder.plateReplenishments
        .map(replenishment => (replenishment.slotNumber == newPlateReplenishment.slotNumber)
        ? {...newPlateReplenishment, id: replenishment.id} : replenishment)

      return updateCart({...draftReplenishmentOrder, plateReplenishments: updatedPlateReplenishments}, ReplenishmentOrderAction.ADD_ITEM)
    } else {
      return updateCart({...draftReplenishmentOrder, plateReplenishments: [...draftReplenishmentOrder.plateReplenishments, newPlateReplenishment]}, ReplenishmentOrderAction.ADD_ITEM)
    }
  }

  const headerContent = () => {
    if(cartLine)  {
      return (
        <>
          <h3 className='header'>{t('bowl_stand', {scope, slotNumber: HUMAN_SLOTS[containerSlot - 1]})}</h3>
          <span className='replenishment-header-text'>to refill</span>
        </>
      )
    }

    return (<h3 className='header'>{t('bowl_stand', {scope, slotNumber: HUMAN_SLOTS[containerSlot - 1]})}</h3>)
  }

  const error = validationError() || saveError

  return (
    <Modal onClose={closeModal} widen='true' headerContent={completed ? null : headerContent()}>
      {completed ?
        <div className='container-modal show'>
          <ReplenishmentCompletedModalContent
            closeModal={closeModal}
            toggleSidebar={toggleSidebar}
            amount={replenishmentAmount}
            slotNumber={containerSlot}
          />
        </div> :
        <BowlStandForm
          remainingAmount={amountLeft}
          maxAmount={maxAmount}
          newTotal={amountLeft + replenishmentAmount}
          amount={replenishmentAmount}
          onAmountChange={changeReplenishmentAmount}
          mode='add'
          notice={alreadyOnOrder > 0 &&
            <div className='plate-stand-notice'>{t('already_on_order', { scope, amount: alreadyOnOrder })}</div>
          }
          error={error}
          onCancel={closeModal}
          onSave={saveReplenishment}
          saveDisabled={replenishmentAmount <= 0 || !!error}
        />
      }
    </Modal>
  )
}
